import { useCallback, useMemo, useState } from 'react'
import { FiInfo, FiRefreshCcw, FiX } from 'react-icons/fi'
import { NumberFormatValues } from 'react-number-format'

import { t } from 'i18next'

import { Button } from '~/components/Buttons'
import Tooltip from '~/components/Tooltip'
import { InputMoney } from '~/components/unform'
import { formatMoney } from '~/helpers'
import { useAppSelector } from '~/store/hooks'

import * as S from './styles'

type EditCostProps = {
  minCost: number | string
  adCost: number
  isValidCost: boolean
  onCancel: () => void
  showMin: boolean
  onEditRequest: (cost: number | null, origin?: BidEditOrigin) => void
  origin?: BidEditOrigin
  localLoading: boolean
  initialCost?: number
}

export const EditCost = ({
  minCost,
  adCost,
  isValidCost,
  onCancel,
  showMin,
  onEditRequest,
  origin,
  localLoading,
  initialCost
}: EditCostProps) => {
  const [cost, setCost] = useState(() => {
    const value = Number(initialCost)
    return Number.isFinite(value) ? value : 0
  })

  const {
    industry: {
      ad: {
        cpcPatch: { loading: loadingCpc },
        cpmPatch: { loading: loadingCpm }
      }
    }
  } = useAppSelector(state => state.retailMedia)

  const loading = useMemo(
    () => loadingCpc || loadingCpm,
    [loadingCpc, loadingCpm]
  )

  const handleChangeValueInput = useCallback(
    (inputData: NumberFormatValues) => {
      const formatNumber = inputData.floatValue / 100
      setCost(formatNumber)
    },
    []
  )

  const showRestoreCostButton = useMemo(
    () => !!adCost && !isValidCost && !origin,
    [adCost, isValidCost, origin]
  )

  const isBelowMinimum = useMemo(() => {
    const min = Number(minCost)
    return Number.isFinite(min) && cost < min
  }, [cost, minCost])

  const handleUseValueDefault = useCallback(() => {
    onEditRequest(null)
  }, [onEditRequest])

  return (
    <S.FormStack>
      <S.WrapperInput>
        <InputMoney
          id="budget"
          name="budget"
          onChange={handleChangeValueInput}
          htmlRightIcon={
            isBelowMinimum && (
              <Tooltip
                icon={<FiInfo className="tooltip-icon" />}
                text={t('rm:components.editBudget.aboveMinimumValue')}
              />
            )
          }
          showBorderInvalid={isBelowMinimum}
          loading={loading}
        />
        {showMin && isBelowMinimum && (
          <S.SubtitleSmall isDanger>
            {t('rm:minimumValue')} {formatMoney(minCost)}
          </S.SubtitleSmall>
        )}
      </S.WrapperInput>

      <S.WrapperButtons>
        <Button
          template="critical"
          onClick={onCancel}
          text={<FiX />}
          aria-label={t('rm:button.Cancel')}
          size="xsmall"
          customWidth="1.75rem"
          disabled={localLoading}
        />

        <Button
          type="submit"
          template="primary"
          disabled={localLoading}
          loading={localLoading}
          text={t('rm:apply')}
          textLoading={t('rm:saving')}
          size="xsmall"
          customWidth="auto"
        />
      </S.WrapperButtons>

      {showRestoreCostButton && (
        <S.ButtonCPCDefault>
          <Tooltip
            noMargin
            icon={
              <Button
                iconLeft={<FiRefreshCcw />}
                text={<small>{t('c:actions.reset')}</small>}
                textLoading={t('rm:saving')}
                template="secondary"
                size="xsmall"
                onClick={handleUseValueDefault}
                loading={localLoading}
                disabled={localLoading}
                customWidth="auto"
              />
            }
            text={t('rm:components.editBudget.useCampaignValue')}
          />
        </S.ButtonCPCDefault>
      )}
    </S.FormStack>
  )
}
